import { auth, db } from '../../src/firebase.js';
import { collection, onSnapshot, getDocs, addDoc, doc, updateDoc, deleteDoc } from 'firebase/firestore';

/**
 * Manages the Shopping Bag drawer and handles the checkout process.
 */
export class CartDrawer {
    constructor() {
        this.isOpen = false;
        this.container = null;
        this.overlay = null;
        this.isTestMode = import.meta.env.VITE_STRIPE_TEST_MODE === 'true';
    }

    init() {
        this.container = document.createElement('div');
        this.container.className = 'fg-cart-drawer';
        this.overlay = document.createElement('div');
        this.overlay.className = 'fg-cart-overlay';
        
        document.body.appendChild(this.overlay);
        document.body.appendChild(this.container);
        
        this.render();
        this.bindEvents();
        this._initGlobalCartLoader();
    }

    _initGlobalCartLoader() {
        auth.onAuthStateChanged(async (user) => {
            if (user) {
                console.log("[Cart] User logged in, attaching real-time listener...");
                const cartRef = collection(db, 'users', user.uid, 'cart');
                onSnapshot(cartRef, (snap) => {
                    const count = snap.docs.reduce((sum, doc) => sum + (doc.data().quantity || 1), 0);
                    console.log(`[Cart] Count updated: ${count}`);
                    window.dispatchEvent(new CustomEvent('fg:cartupdated', { detail: { count } }));
                });
            } else {
                console.log("[Cart] User logged out, resetting count.");
                window.dispatchEvent(new CustomEvent('fg:cartupdated', { detail: { count: 0 } }));
            }
        });
    }

    open() {
        this.isOpen = true;
        this.container.classList.add('is-open');
        this.overlay.classList.add('is-active');
        this.render();
    }

    toggle() {
        this.isOpen = !this.isOpen;
        this.container.classList.toggle('is-open', this.isOpen);
        this.overlay.classList.toggle('is-active', this.isOpen);
        if (this.isOpen) this.render();
    }

    async render() {
        const user = auth.currentUser;
        let content = '';

        if (!user) {
            content = `
                <div class="fg-cart-empty">
                    <p>Please log in to view your bag.</p>
                    <button class="fg-btn fg-btn--primary" id="fg-cart-login" style="margin-top:1rem;">Sign In</button>
                </div>`;
        } else {
            try {
                const cartSnap = await getDocs(collection(db, 'users', user.uid, 'cart'));
                const items = cartSnap.docs.map(d => ({ cartId: d.id, ...d.data() }));
                
                if (items.length === 0) {
                    content = `
                        <div class="fg-cart-empty">
                            <p>Your bag is empty.</p>
                            <a href="#" class="fg-cart-empty__link" id="fg-cart-go-shop">Start Shopping</a>
                        </div>`;
                } else {
                    const totalVal = items.reduce((sum, item) => sum + ((item.priceValue || 0) * item.quantity), 0);
                    const currency = items[0]?.price.match(/[£$€]/)?.[0] || '£';
                    
                    const itemsHtml = items.map(item => `
                        <div class="fg-cart-item">
                            <img src="${item.image}" class="fg-cart-item__img" alt="${item.name}">
                            <div class="fg-cart-item__details">
                                <div class="fg-cart-item__name">${item.name}</div>
                                <div class="fg-cart-item__size">Size: ${item.size}</div>
                                <div class="fg-cart-item__actions">
                                    <div class="fg-cart-item__qty-controls">
                                        <button class="fg-qty-btn" data-action="dec" data-id="${item.cartId}" data-qty="${item.quantity}">-</button>
                                        <span>${item.quantity}</span>
                                        <button class="fg-qty-btn" data-action="inc" data-id="${item.cartId}" data-qty="${item.quantity}">+</button>
                                    </div>
                                    <button class="fg-cart-item__remove" data-id="${item.cartId}">Remove</button>
                                    <span style="font-weight:700;">${item.price}</span>
                                </div>
                            </div>
                        </div>
                    `).join('');
                    
                    content = `
                        <div class="fg-cart-drawer__body">
                            ${itemsHtml}
                        </div>
                        <div class="fg-cart-drawer__footer">
                            <div class="fg-cart-drawer__subtotal">
                                <span>Subtotal</span>
                                <span>${currency}${totalVal.toFixed(2)}</span>
                            </div>
                            <button class="fg-pd-buy-now fg-cart-drawer__checkout">Checkout</button>
                            <p style="text-align: center; margin-top: 12px; font-size: 0.75rem; color: var(--text-secondary);">
                                By checking out, you agree to our <a href="#page=returns" style="color: inherit; text-decoration: underline;">Returns Policy</a>
                            </p>
                        </div>
                    `;
                }
            } catch (e) {
                content = '<div class="fg-cart-empty"><p>Error loading items.</p></div>';
            }
        }

        this.container.innerHTML = `
            <div class="fg-cart-drawer__header">
                <h2 style="font-size: 1.25rem; font-weight: 700;">Shopping Bag</h2>
                <button class="fg-qty-btn" id="fg-cart-close" style="font-size: 1.25rem; cursor: pointer;">✕</button>
            </div>
            ${content}
        `;
    }

    bindEvents() {
        this.overlay.addEventListener('click', () => this.toggle());

        // Global listener to open drawer when any bag button is clicked
        document.addEventListener('click', (e) => {
            if (e.target.closest('.fg-shop-bag-btn')) {
                if (!this.isOpen) this.toggle();
            }
        });

        this.container.addEventListener('click', async (e) => {
            if (e.target.id === 'fg-cart-close') this.toggle();
            if (e.target.id === 'fg-cart-login') { this.toggle(); window.app.handleNavigation('login'); }
            if (e.target.id === 'fg-cart-go-shop') { e.preventDefault(); this.toggle(); window.dispatchEvent(new CustomEvent('fg:navigate', { detail: { page: 'shop' } })); }
            
            // Handle Quantity Updates
            const qtyBtn = e.target.closest('.fg-qty-btn');
            if (qtyBtn) {
                const { id, action, qty } = qtyBtn.dataset;
                const currentQty = parseInt(qty);
                if (action === 'dec' && currentQty <= 1) return;
                
                await updateDoc(doc(db, 'users', auth.currentUser.uid, 'cart', id), {
                    quantity: currentQty + (action === 'inc' ? 1 : -1)
                });
                this.render(); // Refresh the list
            }

            // Handle Item Removal
            if (e.target.classList.contains('fg-cart-item__remove')) {
                await deleteDoc(doc(db, 'users', auth.currentUser.uid, 'cart', e.target.dataset.id));
                this.render(); // Refresh the list
            }

            if (e.target.classList.contains('fg-cart-drawer__checkout')) {
                const btn = e.target;
                const originalText = btn.textContent;
                try {
                    console.log("[Checkout Flow] Button clicked. Disabling button...");
                    btn.disabled = true;
                    btn.textContent = 'Preparing Checkout...';
                    await this._initiateCheckout();
                } catch (err) {
                    console.error("[Checkout Flow] Fatal error during checkout setup:", err);
                    btn.disabled = false;
                    btn.textContent = originalText;
                    alert(err.message || "Checkout unavailable, please try again.");
                }
            }
        });
    }

    async _initiateCheckout() {
        console.log("[Checkout Flow] Step 1: Initiating auth check...");
        const user = auth.currentUser;
        if (!user) {
            console.error("[Checkout Flow] Auth error: No authenticated user found.");
            throw new Error("Auth check failed. Please sign in again.");
        }
        console.log("[Checkout Flow] Auth verified for user:", user.uid);

        console.log("[Checkout Flow] Step 2: Fetching line items from Firestore cart...");
        const cartSnap = await getDocs(collection(db, 'users', user.uid, 'cart'));
        console.log(`[Checkout Flow] Cart scan complete: ${cartSnap.size} items identified.`);

        const lineItems = cartSnap.docs.map(d => {
            const item = d.data();
            return {
                price_data: { 
                    currency: 'gbp', 
                    product_data: { 
                        name: `${item.name} (${item.size})`, 
                        images: [item.image],
                        metadata: {
                            productId: item.productId || item.id || "",
                            variantId: String(item.variantId || "")
                        }
                    }, 
                    unit_amount: Math.round(item.priceValue * 100) 
                },
                quantity: item.quantity
            };
        });

        const stripeKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || "";
        const isTestKey = stripeKey.startsWith('pk_test');
        console.log(`[Checkout Flow] Stripe Env: ${isTestKey ? 'TEST' : 'LIVE'} Mode`);

        console.log("[Checkout Flow] Step 3: Posting session request to Firestore...");
        const docRef = await addDoc(collection(db, 'customers', user.uid, 'checkout_sessions'), {
            mode: 'payment',
            line_items: lineItems,
            success_url: `${window.location.origin}/#page=order-confirmation&session_id={CHECKOUT_SESSION_ID}`,
            cancel_url: window.location.href,
            shipping_address_collection: {
                allowed_countries: ['GB', 'IE', 'US', 'DE', 'FR', 'ES', 'IT']
            },
            shipping_options: [
                {
                    shipping_rate_data: {
                        type: 'fixed_amount',
                        fixed_amount: {
                            amount: 399,
                            currency: 'gbp',
                        },
                        display_name: 'Standard Delivery',
                        delivery_estimate: {
                            minimum: {
                                unit: 'business_day',
                                value: 5,
                            },
                            maximum: {
                                unit: 'business_day',
                                value: 7,
                            },
                        },
                    },
                },
                {
                    shipping_rate_data: {
                        type: 'fixed_amount',
                        fixed_amount: {
                            amount: 799,
                            currency: 'gbp',
                        },
                        display_name: 'Express Delivery',
                        delivery_estimate: {
                            minimum: {
                                unit: 'business_day',
                                value: 2,
                            },
                            maximum: {
                                unit: 'business_day',
                                value: 3,
                            },
                        },
                    },
                },
            ]
        });
        console.log("[Checkout Flow] Session doc created successfully. Doc ID:", docRef.id);

        return new Promise((resolve, reject) => {
            console.log("[Checkout Flow] Step 4: Attaching listener for checkout URL (15s timeout)...");
            
            const timeout = setTimeout(() => {
                unsubscribe();
                console.error("[Checkout Flow] TIMEOUT: Checkout extension failed to provide a URL within 15s.");
                reject(new Error('Checkout unavailable, please try again'));
            }, 15000);

            const unsubscribe = onSnapshot(docRef, async snap => {
                const data = snap.data();
                const url = data?.url;
                const error = data?.error;

                if (url) {
                    console.log("[Checkout Flow] Step 5: SUCCESS! Stripe URL detected. Clearing cart and redirecting...");
                    clearTimeout(timeout);
                    unsubscribe();
                    // Clear cart from Firestore before redirecting
                    try {
                        const cartRef = collection(db, 'users', user.uid, 'cart');
                        const cartSnap = await getDocs(cartRef);
                        const deletePromises = cartSnap.docs.map(d => deleteDoc(doc(db, 'users', user.uid, 'cart', d.id)));
                        await Promise.all(deletePromises);
                        console.log("[Checkout Flow] Cart cleared successfully.");
                    } catch (clearErr) {
                        console.warn("[Checkout Flow] Cart clear failed (non-fatal):", clearErr);
                    }
                    window.location.assign(url);
                    resolve();
                } else if (error) {
                    console.error("[Checkout Flow] ERROR: Stripe extension returned an error:", error);
                    clearTimeout(timeout);
                    unsubscribe();
                    reject(new Error(error.message || "Stripe session error"));
                } else {
                    console.log("[Checkout Flow] Snapshot update received (waiting for URL)...");
                }
            }, (err) => {
                console.error("[Checkout Flow] Listener failed:", err);
                clearTimeout(timeout);
                reject(err);
            });
        });
    }
}